import React, { useState, useEffect, Fragment, useRef } from "react"
import {
  Block,
  Card,
  ProductCard,
  Grid,
  Filter,
  GalleryItem
} from "@components"
import PropTypes from "prop-types"
import ReviewCard from "./review-card"
import GalleryNewGroupCard from "./gallery-new-group-card"
import { JsonLd } from "react-schemaorg"

const FilteredList = ({
  items = [],
  filters = [],
  defaultQueries = new Map(),
  type = "product",
  onChange = () => {},
  showFilters = true,
  onCardClick,
  tiles,
  itemsVisible = 18,
  isCollection = false,
  listSchema = null
}) => {
  const [queries, setQueries] = useState(defaultQueries)
  const [filteredItems, setFilteredItems] = useState([...items])
  const [visibleItems, setVisibleItems] = useState(itemsVisible)
  const observer = useRef()

  // Find the clearanceTag filter
  const clearanceTagFilter = filters.find(
    filter => filter.name === "clearanceTag"
  )

  // Extract options from clearanceTag filter
  const clearanceTagOptions = clearanceTagFilter?.options || []

  useEffect(() => {
    filterContent()
  }, [queries, items])

  // Lazy load more items when the user scrolls to the bottom
  useEffect(() => {
    if (observer.current) observer.current.disconnect()

    const handleObserver = entries => {
      const target = entries[0]
      if (target.isIntersecting) {
        console.log("Bottom of the list reached, loading more items")
        setVisibleItems(prevVisibleItems => prevVisibleItems + itemsVisible)
      }
    }

    observer.current = new IntersectionObserver(handleObserver)
    const loadMoreTrigger = document.querySelector("#load-more-trigger")
    if (loadMoreTrigger) observer.current.observe(loadMoreTrigger)

    return () => observer.current.disconnect()
  }, [filteredItems])

  const filterItems = allItems => {
    return allItems.filter(item => {
      if (queries.size < 1) return true

      const filterFields = [...queries.keys()]

      for (let i = 0; i < filterFields.length; i++) {
        const type = filterFields[i]
        const value = queries.get(type)
        if (!value) continue

        // Custom tag filter: look for a customTags array with a tag.slug === type and value === value
        if (Array.isArray(item.customTags) && item.customTags.length > 0) {
          const isCustomTag = item.customTags.some(
            t => t.tag && t.tag.slug === type
          )
          if (isCustomTag) {
            if (
              !item.customTags.some(
                t => t.tag && t.tag.slug === type && t.value === value
              )
            ) {
              return false
            }
            continue
          }
        }

        // Default filter logic
        const itemValue = item[type]
        if (!itemValue) return false
        if (Array.isArray(itemValue)) {
          if (itemValue.find(({ slug }) => slug === value) === undefined)
            return false
        } else if (itemValue.slug && itemValue.slug !== value) {
          return false
        } else if (!itemValue.slug && itemValue !== value) {
          return false
        }
      }
      return true
    })
  }

  const updateQueries = ({ target }) => {
    const newQuery = new Map(queries)
    newQuery.set(target.name, target.value) // Update the specific filter key
    setQueries(newQuery) // Update the state
  }

  const filterContent = () => {
    let filtered = [...items]

    // Apply other filters
    filtered = filterItems(filtered)

    // Custom Tag filter
    if (queries.has("customTag") && queries.get("customTag")) {
      const selectedSlug = queries.get("customTag")
      filtered = filtered.filter(
        item =>
          Array.isArray(item.customTags) &&
          item.customTags.some(t => t.tag && t.tag.slug === selectedSlug)
      )
    }

    setFilteredItems(filtered)
    onChange(filtered, queries)
  }

  const clearFilter = () => {
    const clearedQueries = new Map()
    setQueries(clearedQueries)
    onChange(items, clearedQueries)
    setFilteredItems(items)
  }

  const renderType = item => {
    switch (type) {
      case "gallery":
        return <GalleryItem {...item} items={filteredItems} />
      case "gallery_new":
        return <GalleryNewGroupCard {...item} items={filteredItems} />
      case "blog":
        return <Card {...item} truncate={3} />
      case "birdeye":
        return <ReviewCard {...item} />
      default:
        return <ProductCard {...item} onClick={() => onCardClick(item)} />
    }
  }

  const renderDisplayText = () => {
    switch (type) {
      case "gallery":
        return "Images"
      case "gallery_new":
        return "Gallery"
      case "blog":
        return "Articles"
      default:
        return "Products"
    }
  }

  let gridStyle = "3-up-grid"
  if (type === "blog") gridStyle = "2-up-grid"
  if (type === "gallery") gridStyle = "3-up-masonary-grid"
  if (type === "gallery_new") gridStyle = "3-up-grid"

  const initialValues = Object.fromEntries(queries)

  return (
    <>
      {filters.length > 0 && showFilters && (
        <Block gutters>
          <form
            onSubmit={e => {
              e.preventDefault()
              filterContent(e)
            }}
          >
            <Filter
              onClick={clearFilter}
              onChange={updateQueries}
              filters={filters.map(filter => ({
                ...filter,
                value: initialValues[filter.name] || ""
              }))}
              displayText={renderDisplayText()}
              tiles={tiles}
              locationOptions={clearanceTagOptions} // Pass clearanceTag options
            />
          </form>
        </Block>
      )}
      {filteredItems.length > 0 ? (
        <Block
          padding={filters.length > 0 && showFilters ? "double-top" : "None"}
        >
          <Grid moduleStyle={gridStyle}>
            {filteredItems.slice(0, visibleItems).map(item => {
              if (!item.hidden) {
                return (
                  <Fragment key={`card--${item.id}`}>
                    {renderType(item)}
                  </Fragment>
                )
              }
            })}
          </Grid>
        </Block>
      ) : (
        <Block padding="double" gutters>
          <center>
            Sorry, we can't find an exact match for your search. Please try
            again.
          </center>
        </Block>
      )}
      {filteredItems.length > visibleItems && (
        <div id="load-more-trigger" style={{ height: "1px" }}></div>
      )}
      {isCollection && listSchema && <JsonLd item={listSchema} />}
    </>
  )
}

FilteredList.propTypes = {
  items: PropTypes.array,
  filters: PropTypes.array,
  defaultQueries: PropTypes.object,
  type: PropTypes.string,
  onChange: PropTypes.func,
  showFilters: PropTypes.bool,
  onCardClick: PropTypes.func,
  tiles: PropTypes.array,
  itemsVisible: PropTypes.number,
  isCollection: PropTypes.bool,
  listSchema: PropTypes.object
}

export default FilteredList
